import { useEffect, useId, useRef, useState } from 'react';
import { t, tn } from '@app/i18n';
import { Alert, Button, ErrorBanner, Field, LoadingButton, TextArea } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { changeLabel, changeValue, type AdmissionPreview } from './SiteAdmissionModel';
import dialog from '../Sites.module.css';
import s from './SiteAdmission.module.css';

export interface AdmissionApplyDialogProps {
  siteName: string;
  preview: AdmissionPreview;
  pending: boolean;
  error?: ApiError | null;
  onApply: (reason: string) => void;
  onCancel: () => void;
}

/**
 * «Revisar e aplicar» (protótipo §3): o que muda em relação à última versão salva, o aviso de impacto da assinatura
 * (decisão 4) e o motivo, obrigatório em toda aplicação (decisão 3). O envio vale para todos os pods novos do
 * cluster, por isso nada sai sem este diálogo.
 *
 * Diálogo próprio, e não o `ConfirmDialog`, pela largura (02/10, mrolim): o diff tem valores longos (template do
 * SPIFFE ID, imagem com digest) e, nos 560 px do diálogo de confirmação, cada linha quebrava em três. Aqui a caixa é
 * larga, a tabela rola sozinha, e o motivo e os botões ficam sempre à vista.
 */
export function AdmissionApplyDialog({ siteName, preview, pending, error, onApply, onCancel }: AdmissionApplyDialogProps) {
  const [reason, setReason] = useState('');
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const first = preview.base_version === 0;

  useEffect(() => {
    cancelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pending) onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, pending]);

  return (
    <div className={dialog.overlay}>
      <div className={s.applyBox} role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid="admission-apply">
        <div className={dialog.mh}>
          <h3 id={titleId}>{t('sites.admission.apply.title', { version: preview.next_version, site: siteName })}</h3>
        </div>
        <div className={s.applyBody}>
          {error ? <ErrorBanner error={error} title={t('sites.admission.apply.error')} hideLink /> : null}
          <p>
            {first
              ? t('sites.admission.apply.changes.first')
              : t('sites.admission.apply.changes', { version: preview.base_version })}
          </p>
          {preview.changes.length === 0 ? (
            <p className={s.note}>{t('sites.admission.apply.noChanges')}</p>
          ) : (
            <div className={s.diffScroll}>
              <table className={s.applyDiff} aria-label={t('sites.admission.apply.diffLabel')}>
                <thead>
                  <tr>
                    <th scope="col" className={s.applyFieldColumn}>
                      {t('sites.admission.apply.column.field')}
                    </th>
                    {/* Na primeira versão não há «antes»: a coluna sairia inteira com «—». */}
                    {first ? (
                      <th scope="col">{t('sites.admission.apply.column.value')}</th>
                    ) : (
                      <>
                        <th scope="col">{t('sites.admission.apply.column.before')}</th>
                        <th scope="col">{t('sites.admission.apply.column.after')}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {preview.changes.map((change) => (
                    <tr key={change.field} data-field={change.field}>
                      <td>{changeLabel(change.field)}</td>
                      {first ? null : <td className={s.applyValue}>{changeValue(change.field, change.before)}</td>}
                      <td className={s.applyValue}>{changeValue(change.field, change.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <ImpactNotice impact={preview.impact} />
          <p className={s.note}>{t('sites.admission.apply.podsNote')}</p>
          <div className={s.reason}>
            <Field label={t('sites.admission.apply.reason')} span={12} required>
              {(props) => (
                <TextArea
                  {...props}
                  rows={2}
                  value={reason}
                  placeholder={t('sites.admission.apply.reason.placeholder')}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Field>
          </div>
        </div>
        <div className={dialog.mf}>
          <Button ref={cancelRef} variant="out" onClick={onCancel} disabled={pending}>
            {t('sites.admission.apply.cancel')}
          </Button>
          <LoadingButton
            loading={pending}
            disabled={reason.trim() === ''}
            loadingLabel={t('sites.admission.apply.applying')}
            onClick={() => onApply(reason.trim())}
          >
            {t('sites.admission.apply.confirm')}
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

/**
 * O aviso de impacto. Ele é informação: a regra quem faz cumprir é o admission, e por isso uma conferência que não
 * rodou diz isso e deixa aplicar, em vez de travar o operador por um problema de rede.
 */
function ImpactNotice({ impact }: { impact: AdmissionPreview['impact'] }) {
  if (impact.state === 'not_applicable') return null;
  if (impact.state === 'unavailable') {
    return (
      <Alert tone="info" title={t('sites.admission.impact.unavailable.title')}>
        {t('sites.admission.impact.unavailable.message', { code: impact.code ?? '—' })}
      </Alert>
    );
  }
  if (impact.would_be_refused === 0 && impact.unverifiable === 0) {
    return (
      <p className={s.note} data-testid="admission-impact">
        {tn('sites.admission.impact.none', impact.total, { n: impact.total })}
      </p>
    );
  }
  return (
    <Alert tone="warning" title={t('sites.admission.impact.title')}>
      <span data-testid="admission-impact">
        {impact.would_be_refused > 0
          ? tn('sites.admission.impact.refused', impact.would_be_refused, {
              n: impact.would_be_refused,
              total: impact.total,
            })
          : null}
        {impact.unverifiable > 0 ? (
          <> {tn('sites.admission.impact.unverifiable', impact.unverifiable, { n: impact.unverifiable })}</>
        ) : null}
      </span>
    </Alert>
  );
}
